import { Button } from "@egaranti/components";
import { Input } from "@egaranti/components";
import { Checkbox } from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ProductService from "@/services/productService";

import { SearchIcon } from "lucide-react";

const ProductSelector = ({ selectedProducts, onProductsChange }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await ProductService.getProducts({
        page: 1,
        size: 5,
        query: searchQuery,
      });
      setProducts(data.content || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchProducts();
  };

  const handleToggleProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      onProductsChange(selectedProducts.filter((id) => id !== productId));
    } else {
      onProductsChange([...selectedProducts, productId]);
    }
  };

  const handleToggleAll = () => {
    if (selectedProducts.length === products.length) {
      onProductsChange([]);
    } else {
      onProductsChange(products.map((p) => p.id));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder={t("campaignsPage.searchProducts")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-10"
          />
        </div>
        <Button variant="secondaryGray" onClick={handleSearch} type="button">
          {t("common.search")}
        </Button>
      </div>

      <div className="text-sm text-[#677389]">
        {t("campaignsPage.productsSelected", {
          count: selectedProducts.length,
          total: products.length,
        })}
      </div>

      {products.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <Checkbox
              checked={
                products.length > 0 &&
                selectedProducts.length === products.length
              }
              onCheckedChange={handleToggleAll}
            />
            <span className="text-sm font-medium text-[#101828]">
              {t("common.selectAll")}
            </span>
          </div>

          {products.map((product) => (
            <div
              key={product.id}
              className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors ${
                selectedProducts.includes(product.id)
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedProducts.includes(product.id)}
                  onCheckedChange={() => handleToggleProduct(product.id)}
                />
                <div>
                  <p className="font-medium text-[#101828]">{product.name}</p>
                  <p className="text-sm text-[#677389]">
                    {product.brand} • {product.category}
                  </p>
                </div>
              </div>
              {selectedProducts.includes(product.id) && (
                <span className="text-sm font-medium text-blue-600">
                  {t("common.selected")}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="py-8 text-center">
          <p className="text-[#677389]">{t("common.loading")}</p>
        </div>
      )}

      {!loading && products.length === 0 && (
        <div className="py-8 text-center">
          <p className="text-[#677389]">{t("campaignsPage.noProductsFound")}</p>
        </div>
      )}
    </div>
  );
};

export default ProductSelector;
